import { Chat } from "@/types/ApiResponses/Chat";
import { Message } from "@/types/ApiResponses/Message";
import { Socket } from "socket.io-client";

export interface BalanceUpdatePayload {
  userId: number;
  balance: number;
}

export interface NewMessagePayload {
  chat: Chat;
  message: Message;
}

export interface MessagesReadPayload {
  chatId: number;
  readBy: number;
}

export interface AppSettingUpdatePayload {
  key: string;
  value: unknown;
}

export interface UserTypingPayload {
  chatId: number;
  userId: number;
  username: string;
}

export interface TelegramLinkedPayload {
  userId: number;
}

// Mirror of sinek-api `SessionEndedReason`. Drives cross-tab + cross-socket
// log-out behaviour. New values must be added in BOTH frontend and backend
// socketTypes.
export type SessionEndedReason =
  | "user_logout"
  | "remote_terminate"
  | "expired";

export interface SessionEndedPayload {
  sessionId: string;
  reason: SessionEndedReason;
}

// Mirror of sinek-api. Admin sport-event mutation (suspensions / provider
// settings / league-country catalog) → refetch the relevant admin tables.
export interface SportEventsChangedPayload {
  scope: string;
}

// Mirror of sinek-api. A coupon was created in this viewer's downline → refresh
// coupon lists.
export interface CouponsChangedPayload {
  userId: number;
}

// Mirror of sinek-api. A not-settled risk-class match's site-wide total bet
// crossed the configured risk threshold.
export interface RiskyMatchAlertPayload {
  matchId: string;
  providerId: number;
  providerMatchId: number;
  name: string | null;
  totalStake: number;
  threshold: number;
}

// Mirror of sinek-api (DEALER-EXCHANGE-PLAN §9.4). The book of one market in
// the subscriber's pool changed: refetch GET /exchange/markets/:id/book (a
// server cache hit). Coalesced per market to 250 ms on the server.
export interface ExchangeBookChangedPayload {
  marketId: string;
  bookSeq: string;
}

// Mirror of sinek-api. The player's own orders in a market changed (placed,
// filled, cancelled, lapsed); sent to the `user:` room.
export interface ExchangeOrdersChangedPayload {
  marketId: string;
}

// Mirror of sinek-api. The server resolves the player's pool and joins the
// (pool, market) room; at most 10 exchange rooms per socket.
export interface ExchangeSubscribePayload {
  marketId: string;
}

export type ExchangeSubscribeAck =
  | { ok: true; marketId: string }
  | { ok: false; code: string };

export interface ServerToClientEvents {
  updateBalance: (data: BalanceUpdatePayload) => void;
  newMessage: (data: NewMessagePayload) => void;
  messagesRead: (data: MessagesReadPayload) => void;
  updateAppSetting: (data: AppSettingUpdatePayload) => void;
  userTyping: (data: UserTypingPayload) => void;
  telegramLinked: (data: TelegramLinkedPayload) => void;
  telegramNotificationsLinked: (data: TelegramLinkedPayload) => void;
  sessionEnded: (data: SessionEndedPayload) => void;
  sportEventsChanged: (data: SportEventsChangedPayload) => void;
  couponsChanged: (data: CouponsChangedPayload) => void;
  riskyMatchAlert: (data: RiskyMatchAlertPayload) => void;
  exchangeBookChanged: (data: ExchangeBookChangedPayload) => void;
  exchangeOrdersChanged: (data: ExchangeOrdersChangedPayload) => void;
}

export interface ClientToServerEvents {
  heartbeat: () => void;
  typing: (data: { chatId: number }) => void;
  exchangeSubscribe: (
    data: ExchangeSubscribePayload,
    ack: (result: ExchangeSubscribeAck) => void
  ) => void;
  exchangeUnsubscribe: (
    data: ExchangeSubscribePayload,
    ack: (result: ExchangeSubscribeAck) => void
  ) => void;
}

export type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export enum ConnectionStatus {
  Disconnected = "disconnected",
  Connecting = "connecting",
  Connected = "connected",
}

export interface SocketState {
  status: ConnectionStatus;
  socket: TypedSocket | null;
}
